import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import advancedFormat from "dayjs/plugin/advancedFormat";
import "dayjs/locale/sv";
import { DATE_TIME_FORMAT } from "@lib/constants/system";
import { useRemoteConfigStore } from "@/store/remoteConfig";
import { DAYS_MAP, type TDisplayPeriod } from "@/constants/periods";

dayjs.extend(duration);
dayjs.extend(advancedFormat);

export { sleep } from "@lib/utils/time";

/**
 * Formats a date string using dayjs.
 * @param dateString The date string to format.
 * @param format The dayjs format string (e.g., "YYYY-MM-DD HH:mm", "L LT"). Defaults to "YYYY-MM-DD HH:mm".
 * @returns The formatted date string or "Invalid Date" or "N/A".
 */
export const formatDateTime = (
    dateString: Date | string | undefined,
    format: string = DATE_TIME_FORMAT,
    fallback: string = "N/A",
): string => {
    if (!dateString) return fallback;
    const { defaultLocale } = useRemoteConfigStore();

    dayjs.locale(defaultLocale ?? "sv");
    const d = dayjs(dateString);

    if (d.isValid()) {
        return d.format(format);
    } else {
        return "Invalid Date";
    }
};

export const getReadableTimeFromSeconds = (
    seconds: number,
    fallback: string = "N/A",
): string => {
    if (!seconds) return fallback;
    const _duration = dayjs.duration(seconds, "seconds");
    return `${_duration.minutes()}:${`${_duration.seconds()}`.padStart(2, "0")}`;
};

export const getClientTimeZone = () => {
    return Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone ?? "Europe/Stockholm";
};

/**
 * Get today's date in ISO format (YYYY-MM-DD) using local timezone.
 * Uses manual formatting to ensure consistent ISO format regardless of user locale.
 */
export function todayISO(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export const getDaysForPeriod = (period: TDisplayPeriod): number => {
    const value = DAYS_MAP[period];
    return typeof value === "function" ? value() : value;
};
